import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import type { AxiosError } from 'axios'

import { triggerToast } from '@/components/Toast'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogPortal,
  DialogTitle,
} from '@/components/ui/dialog'

import { useDeleteCommentThread } from '../hooks/hooks'
import { useCommentStore } from '../store'

const DeleteCommentThreadDialog: React.FC = () => {
  const { t } = useTranslation()
  const {
    deletingThread,
    deleteDialogOpen,
    setDeleteDialogOpen,
    clearDeletingThread,
    setActiveCommentId,
  } = useCommentStore()

  const deleteMutation = useDeleteCommentThread(
    deletingThread?.threadId || '',
    deletingThread?.documentVersionId || ''
  )

  const handleOpenChange = (newOpen: boolean) => {
    setDeleteDialogOpen(newOpen)
    if (!newOpen) {
      clearDeletingThread()
      deleteMutation.reset()
    }
  }

  const handleConfirm = () => {
    if (!deletingThread) return

    deleteMutation.mutate(undefined, {
      onSuccess: () => {
        triggerToast('success', t('tflViewer.comment.messages.deleteSuccess'))
        setDeleteDialogOpen(false)
        clearDeletingThread()

        setActiveCommentId(undefined)
      },
      onError: (error: unknown) => {
        const axiosError = error as AxiosError<{ message?: string }>
        const errorMessage =
          axiosError.response?.data?.message ||
          axiosError.message ||
          t('tflViewer.comment.messages.errorGeneric')

        triggerToast('error', errorMessage)
      },
    })
  }

  const isDeleting = useMemo(
    () => deleteMutation.isPending,
    [deleteMutation.isPending]
  )

  return (
    <Dialog open={deleteDialogOpen} onOpenChange={handleOpenChange}>
      <DialogPortal>
        <DialogContent className="sm:max-w-[480px] bg-white shadow-xl rounded-2xl p-6">
          <DialogHeader className="space-y-0.5">
            <DialogTitle className="m-0 text-md font-semibold text-primary-900">
              {t('tflViewer.comment.deleteTitle')}
            </DialogTitle>
            <DialogDescription className="text-sm text-tertiary-600">
              {t('tflViewer.comment.deleteDescription')}
            </DialogDescription>
          </DialogHeader>

          <DialogFooter className="flex justify-stretch !space-x-0 gap-lg pt-3">
            <Button
              type="button"
              className="flex-1 rounded-md py-2.5 px-xl font-semibold text-secondary-700 hover:text-secondary-hover text-md border-solid border-primary bg-white hover:bg-primary-hover"
              variant="default"
              onClick={() => handleOpenChange(false)}
              disabled={isDeleting}
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="button"
              className="flex-1 rounded-md py-2.5 px-xl font-semibold text-md border-solid border-transparent !text-white !bg-error-solid disabled:!bg-disabled disabled:!text-disabled"
              variant="default"
              onClick={handleConfirm}
              disabled={isDeleting}
            >
              {t('common.delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </DialogPortal>
    </Dialog>
  )
}

export default DeleteCommentThreadDialog
